import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Settings as SettingsIcon, ChevronDown, ChevronRight, X } from 'lucide-react';

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('checklists');
  const [checklists, setChecklists] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [holdRules, setHoldRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [expandedChecklist, setExpandedChecklist] = useState(null);
  const [editingStage, setEditingStage] = useState(null);
  const [newStageName, setNewStageName] = useState('');
  const [stageItems, setStageItems] = useState([]);
  const [newItemText, setNewItemText] = useState('');

  const [accessoryForm, setAccessoryForm] = useState({
    product_category: 'Laptop',
    accessory_name: '',
    required: true,
  });

  const [ruleForm, setRuleForm] = useState({
    product_category: 'All',
    rule_name: '',
    rule_type: 'keyword',
    condition: { keywords: [] },
    severity: 'high',
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [checklistsRes, accessoriesRes, rulesRes] = await Promise.all([
        api.get('/checklists'),
        api.get('/accessories'),
        api.get('/hold-rules'),
      ]);
      setChecklists(checklistsRes.data);
      setAccessories(accessoriesRes.data);
      setHoldRules(rulesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAccessory = async () => {
    try {
      await api.post('/accessories', accessoryForm);
      toast.success('Accessory added successfully!');
      setShowDialog(false);
      resetForms();
      fetchAll();
    } catch (error) {
      toast.error('Failed to add accessory');
    }
  };

  const handleDeleteAccessory = async (id) => {
    if (!window.confirm('Delete this accessory?')) return;
    try {
      await api.delete(`/accessories/${id}`);
      toast.success('Accessory deleted');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleAddRule = async () => {
    try {
      await api.post('/hold-rules', ruleForm);
      toast.success('Hold rule added!');
      setShowDialog(false);
      resetForms();
      fetchAll();
    } catch (error) {
      toast.error('Failed to add rule');
    }
  };

  const handleToggleRule = async (rule) => {
    try {
      await api.put(`/hold-rules/${rule.id}`, { active: !rule.active });
      toast.success(`Rule ${!rule.active ? 'activated' : 'deactivated'}`);
      fetchAll();
    } catch (error) {
      toast.error('Failed to toggle rule');
    }
  };

  const handleDeleteRule = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    try {
      await api.delete(`/hold-rules/${id}`);
      toast.success('Rule deleted');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleSaveStage = async () => {
    if (!editingStage) return;
    
    const stageName = dialogType === 'add-stage' ? newStageName.trim() : editingStage.stage;
    if (!stageName) {
      toast.error('Stage name is required');
      return;
    }

    try {
      await api.put(`/checklists/${editingStage.product}/stage`, {
        stage: stageName,
        items: stageItems.filter(item => item.trim()),
      });
      toast.success(`Stage "${stageName}" saved successfully`);
      setShowDialog(false);
      setEditingStage(null);
      setStageItems([]);
      setNewStageName('');
      fetchAll();
    } catch (error) {
      console.error('Failed to save stage:', error);
      toast.error('Failed to save stage');
    }
  };

  const handleDeleteStage = async (productCategory, stageName) => {
    if (!window.confirm(`Delete stage "${stageName}"? This cannot be undone.`)) return;
    
    try {
      await api.delete(`/checklists/${productCategory}/stage/${encodeURIComponent(stageName)}`);
      toast.success(`Stage "${stageName}" deleted`);
      fetchAll();
    } catch (error) {
      console.error('Failed to delete stage:', error);
      toast.error('Failed to delete stage');
    }
  };

  const resetForms = () => {
    setAccessoryForm({
      product_category: 'Laptop',
      accessory_name: '',
      required: true,
    });
    setRuleForm({
      product_category: 'All',
      rule_name: '',
      rule_type: 'keyword',
      condition: { keywords: [] },
      severity: 'high',
    });
    setEditingItem(null);
  };

  const productCategories = ['Laptop', 'Desktop', 'Projector', 'Tablet', 'TV', 'Printer', 'PS5', 'Air Purifier'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">Settings</h1>
        <p className="text-lg text-muted-foreground">Manage checklists, accessories, and hold rules</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-2xl grid-cols-3 h-14">
          <TabsTrigger value="checklists" data-testid="tab-checklists">Checklists</TabsTrigger>
          <TabsTrigger value="accessories" data-testid="tab-accessories">Accessories</TabsTrigger>
          <TabsTrigger value="hold-rules" data-testid="tab-hold-rules">Hold Rules</TabsTrigger>
        </TabsList>

        <TabsContent value="checklists" className="mt-6">
          <div className="space-y-4">
            {checklists.map((checklist) => (
              <div key={checklist.id} className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
                <div
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => setExpandedChecklist(expandedChecklist === checklist.id ? null : checklist.id)}
                  data-testid={`checklist-${checklist.product_category}`}
                >
                  <div className="flex items-center space-x-3">
                    {expandedChecklist === checklist.id ? (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    )}
                    <h3 className="text-xl font-bold">{checklist.product_category}</h3>
                    <span className="text-sm text-muted-foreground">
                      ({Object.keys(checklist.checklist_data).length} stages)
                    </span>
                  </div>
                </div>

                {expandedChecklist === checklist.id && (
                  <div className="border-t p-4 space-y-4">
                    {Object.entries(checklist.checklist_data).map(([stage, items]) => (
                      <div key={stage} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-lg font-bold text-primary">{stage}</h4>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingStage({ product: checklist.product_category, stage, items });
                                setStageItems([...items]);
                                setDialogType('edit-stage');
                                setShowDialog(true);
                              }}
                              data-testid={`edit-stage-${stage}`}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteStage(checklist.product_category, stage)}
                              data-testid={`delete-stage-${stage}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <ul className="space-y-1 text-sm">
                          {items.map((item, idx) => (
                            <li key={idx} className="flex items-center space-x-2 text-muted-foreground">
                              <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}

                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        setEditingStage({ product: checklist.product_category, stage: null, items: [] });
                        setNewStageName('');
                        setStageItems([]);
                        setDialogType('add-stage');
                        setShowDialog(true);
                      }}
                      data-testid={`add-stage-${checklist.product_category}`}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add New Stage
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Edit/Add Stage Dialog */}
          <Dialog open={showDialog && (dialogType === 'edit-stage' || dialogType === 'add-stage')} onOpenChange={(open) => {
            setShowDialog(open);
            if (!open) {
              setEditingStage(null);
              setStageItems([]);
              setNewStageName('');
            }
          }}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>
                  {dialogType === 'add-stage' ? 'Add New Stage' : `Edit Stage: ${editingStage?.stage}`}
                </DialogTitle>
                <DialogDescription>
                  {dialogType === 'add-stage' 
                    ? 'Create a new QC stage with checklist items'
                    : 'Modify the checklist items for this stage'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {dialogType === 'add-stage' && (
                  <div>
                    <Label>Stage Name *</Label>
                    <Input
                      value={newStageName}
                      onChange={(e) => setNewStageName(e.target.value)}
                      placeholder="e.g., Physical Inspection"
                      className="h-12 mt-1"
                      data-testid="stage-name-input"
                    />
                  </div>
                )}
                
                <div>
                  <Label>Checklist Items</Label>
                  <div className="space-y-2 mt-2 max-h-60 overflow-y-auto">
                    {stageItems.map((item, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <Input
                          value={item}
                          onChange={(e) => {
                            const newItems = [...stageItems];
                            newItems[idx] = e.target.value;
                            setStageItems(newItems);
                          }}
                          className="flex-1"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setStageItems(stageItems.filter((_, i) => i !== idx))}
                        >
                          <X className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex items-center space-x-2 mt-3">
                    <Input
                      value={newItemText}
                      onChange={(e) => setNewItemText(e.target.value)}
                      placeholder="Add new item..."
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && newItemText.trim()) {
                          setStageItems([...stageItems, newItemText.trim()]);
                          setNewItemText('');
                        }
                      }}
                    />
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (newItemText.trim()) {
                          setStageItems([...stageItems, newItemText.trim()]);
                          setNewItemText('');
                        }
                      }}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveStage}
                  disabled={dialogType === 'add-stage' && !newStageName.trim()}
                  data-testid="save-stage-button"
                >
                  Save Stage
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="accessories" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={showDialog && dialogType === 'accessory'} onOpenChange={(open) => {
              setShowDialog(open);
              if (!open) resetForms();
            }}>
              <DialogTrigger asChild>
                <Button onClick={() => { setDialogType('accessory'); setShowDialog(true); }} data-testid="add-accessory-button">
                  <Plus className="w-5 h-5 mr-2" />
                  Add Accessory
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Accessory</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Product Category</Label>
                    <Select value={accessoryForm.product_category} onValueChange={(v) => setAccessoryForm({ ...accessoryForm, product_category: v })}>
                      <SelectTrigger className="h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {productCategories.map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Accessory Name</Label>
                    <Input value={accessoryForm.accessory_name} onChange={(e) => setAccessoryForm({ ...accessoryForm, accessory_name: e.target.value })} className="h-12" />
                  </div>
                  <div className="flex items-center space-x-3">
                    <Switch checked={accessoryForm.required} onCheckedChange={(v) => setAccessoryForm({ ...accessoryForm, required: v })} />
                    <Label>Required</Label>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                    <Button onClick={handleAddAccessory}>Add</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-bold">Product</th>
                  <th className="px-6 py-4 text-left text-sm font-bold">Accessory</th>
                  <th className="px-6 py-4 text-left text-sm font-bold">Required</th>
                  <th className="px-6 py-4 text-left text-sm font-bold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {accessories.map((acc) => (
                  <tr key={acc.id}>
                    <td className="px-6 py-4">{acc.product_category}</td>
                    <td className="px-6 py-4 font-medium">{acc.accessory_name}</td>
                    <td className="px-6 py-4">{acc.required ? 'Yes' : 'No'}</td>
                    <td className="px-6 py-4">
                      <Button variant="destructive" size="sm" onClick={() => handleDeleteAccessory(acc.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="hold-rules" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={showDialog && dialogType === 'rule'} onOpenChange={(open) => {
              setShowDialog(open);
              if (!open) resetForms();
            }}>
              <DialogTrigger asChild>
                <Button onClick={() => { setDialogType('rule'); setShowDialog(true); }} data-testid="add-rule-button">
                  <Plus className="w-5 h-5 mr-2" />
                  Add Rule
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Hold Rule</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Product Category</Label>
                    <Select value={ruleForm.product_category} onValueChange={(v) => setRuleForm({ ...ruleForm, product_category: v })}>
                      <SelectTrigger className="h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="All">All Products</SelectItem>
                        {productCategories.map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Rule Name</Label>
                    <Input value={ruleForm.rule_name} onChange={(e) => setRuleForm({ ...ruleForm, rule_name: e.target.value })} className="h-12" />
                  </div>
                  <div>
                    <Label>Rule Type</Label>
                    <Select value={ruleForm.rule_type} onValueChange={(v) => setRuleForm({ ...ruleForm, rule_type: v })}>
                      <SelectTrigger className="h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="keyword">Keyword</SelectItem>
                        <SelectItem value="threshold">Threshold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {ruleForm.rule_type === 'keyword' && (
                    <div>
                      <Label>Keywords (comma-separated)</Label>
                      <Input
                        placeholder="overheating, port not working"
                        onChange={(e) => setRuleForm({ ...ruleForm, condition: { keywords: e.target.value.split(',').map(k => k.trim()) } })}
                        className="h-12"
                      />
                    </div>
                  )}
                  <div>
                    <Label>Severity</Label>
                    <Select value={ruleForm.severity} onValueChange={(v) => setRuleForm({ ...ruleForm, severity: v })}>
                      <SelectTrigger className="h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                    <Button onClick={handleAddRule}>Add</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-4">
            {holdRules.map((rule) => (
              <div key={rule.id} className="bg-white rounded-xl border border-border shadow-sm p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-xl font-bold">{rule.rule_name}</h3>
                      <span className={`px-2 py-1 rounded text-xs font-bold ${rule.severity === 'high' ? 'bg-red-100 text-red-700' : rule.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                        {rule.severity}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      <strong>Product:</strong> {rule.product_category} | <strong>Type:</strong> {rule.rule_type}
                    </p>
                    {rule.rule_type === 'keyword' && (
                      <p className="text-sm">
                        <strong>Keywords:</strong> {rule.condition.keywords?.join(', ')}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center space-x-3">
                    <Switch checked={rule.active} onCheckedChange={() => handleToggleRule(rule)} />
                    <Button variant="destructive" size="sm" onClick={() => handleDeleteRule(rule.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
